import { z } from "zod";

export const Uuid = z.uuid();

/** The error code of a name refused for carrying a link: `apps/api` answers with it instead of `validation_failed`. */
export const NAME_HAS_LINK = "name_has_link";

/**
 * A firm's and a person's name go into letters sent from the service's own domain (`{tenant}`, `{inviter}`), so a link in one is a
 * phishing line with a real sender. Bare domains stay allowed: "Pizza.ro" is a real firm's name.
 */
export const withoutLink = <T extends z.ZodString>(name: T): T => name.refine((value) => !/:\/\/|www\./i.test(value), { params: { code: NAME_HAS_LINK } }) as T;

export const ErrorEnvelope = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ErrorEnvelope = z.infer<typeof ErrorEnvelope>;

export const MAX_PAGE_LIMIT = 100;
export const DEFAULT_PAGE_LIMIT = 25;

export const PageQuery = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_LIMIT).default(DEFAULT_PAGE_LIMIT),
});
export type PageQuery = z.infer<typeof PageQuery>;

/** For lists ordered by a UUIDv7 id, where the cursor is that id. A malformed cursor is a 400, not a database error. */
export const IdPageQuery = PageQuery.extend({ cursor: Uuid.optional() });

export function pageOf<T extends z.ZodType>(item: T) {
  return z.object({ items: z.array(item), nextCursor: z.string().nullable() });
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

export const IsoDateTime = z.iso.datetime();
